// App.jsx
import React, { useState } from 'react';
import {
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { AuthProvider, useAuth } from 'react-oidc-context';
import {isMaintenanceMode} from './utilities/constants.jsx';

import theme from './theme';
import { UserPoolClientId, HostedUIUrl, Authority } from './utilities/constants';

import LandingPage from './pages/LandingPage';

import MainApp from './MainApp';
import CallbackPage from './pages/CallbackPage'; 
import MaintenancePage from './pages/MaintenancePage';

const isLocalDev =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1');

// Determine base URL from current window location to avoid redirect_uri mismatches
const getBaseUrl = () => {
  if (typeof window === 'undefined') {
    // Fallback for non-browser environments
    return HostedUIUrl || 'http://localhost:3000';
  }

  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `http://localhost:${port || '3000'}`;
  }

  // Use the exact origin (handles www / non-www correctly)
  return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
};

// Component to redirect to standalone HTML estimator
function EstimatorRedirect() {
  React.useEffect(() => {
    // Force full page load of the standalone HTML file
    window.location.href = '/AccessWCAGCostEstimator.html';
  }, []);
  return <div>Redirecting to Cost Estimator...</div>;
}

const baseUrl = getBaseUrl();

// Validate required environment variables
const validateAuthConfig = () => {
  const errors = [];
  if (!Authority) errors.push('REACT_APP_AUTHORITY');
  if (!UserPoolClientId) errors.push('REACT_APP_USER_POOL_CLIENT_ID');
  if (!HostedUIUrl && !isLocalDev) errors.push('REACT_APP_HOSTED_UI_URL');
  
  if (errors.length > 0) {
    console.error('Missing required environment variables:', errors.join(', '));
    console.error('Please create a .env file in pdf_ui/ directory with the required values.');
    console.error('See config.env.example for reference.');
  }
  return errors.length === 0;
};

// Check if we're in development mode with placeholder values
const isPlaceholderConfig = (Authority && Authority.includes('PLACEHOLDER')) || 
                           (UserPoolClientId && UserPoolClientId.includes('PLACEHOLDER'));
const isDevMode = process.env.REACT_APP_DEV_MODE === 'true' || isPlaceholderConfig;

// Build auth config only if required values are present and not placeholders
const buildAuthConfig = () => {
  // Skip auth config in dev mode with placeholders - we'll handle auth differently
  if (isDevMode && isPlaceholderConfig) {
    return null; // Return null to skip AuthProvider initialization
  }
  
  if (!Authority || !UserPoolClientId) {
    return null;
  }
  
  const redirectUri = `${baseUrl}/callback`;
  const postLogoutRedirectUri = `${baseUrl}/home`;
  
  // // Log for debugging - helps identify redirect URI mismatches
  // console.log('Auth Config:', {
  //   baseUrl,
  //   redirectUri,
  //   postLogoutRedirectUri,
  //   currentOrigin: typeof window !== 'undefined' ? window.location.origin : 'N/A'
  // });
  
  return {
    authority: `https://${Authority}`,
    client_id: UserPoolClientId,
    redirect_uri: redirectUri,
    post_logout_redirect_uri: postLogoutRedirectUri,
    response_type: 'code',
    scope: 'email openid phone profile',
    // Disable automatic silent renew to avoid state conflicts
    automaticSilentRenew: false,
    loadUserInfo: true,
  };
};

const cognitoAuthConfig = buildAuthConfig();
const isConfigValid = cognitoAuthConfig !== null && validateAuthConfig();

function AppRoutes() {
  const auth = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Handle case where AuthProvider might not be properly initialized
  if (!isConfigValid) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center',
        fontFamily: 'Arial, sans-serif',
        maxWidth: '600px',
        margin: '50px auto'
      }}>
        <h2 style={{ color: '#d32f2f', marginBottom: '1rem' }}>Configuration Error</h2>
        <p style={{ marginBottom: '1rem' }}>
          Missing required environment variables for authentication.
        </p>
        <p style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#666' }}>
          Please create a <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>.env</code> file 
          in the <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>pdf_ui/</code> directory 
          with the required AWS Cognito configuration values.
        </p>
        <p style={{ fontSize: '0.85rem', color: '#666' }}>
          See <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>config.env.example</code> 
          in the parent directory for reference.
        </p>
        <p style={{ marginTop: '1.5rem', fontSize: '0.85rem', color: '#999' }}>
          Required variables: REACT_APP_AUTHORITY, REACT_APP_USER_POOL_CLIENT_ID, REACT_APP_HOSTED_UI_URL
        </p>
      </div>
    );
  }

  if (auth.isLoading) {
    return <div>Loading authentication status...</div>;
  }

  // In dev mode, ignore auth errors and show UI anyway
  if (isDevMode && auth.error) {
    console.warn('Development mode: Authentication disabled due to placeholder credentials. Showing UI for testing.');
    // Continue to show routes - auth errors are expected in dev mode
  } else if (auth.error && !isDevMode) {
    console.error('Authentication error:', auth.error);

    // // Extra debug for "No matching state found" issues
    // if (auth.error.message && auth.error.message.includes('No matching state')) {
    //   try {
    //     const hasWindow = typeof window !== 'undefined';
    //     const debugInfo = {
    //       errorMessage: auth.error.message,
    //       currentUrl: hasWindow ? window.location.href : 'N/A',
    //       currentOrigin: hasWindow ? window.location.origin : 'N/A',
    //       redirectUri: `${baseUrl}/callback`,
    //       postLogoutRedirectUri: `${baseUrl}/home`,
    //       authUserPresent: !!auth.user,
    //       authUserProfile: auth.user?.profile,
    //     };

    //     if (hasWindow && window.sessionStorage) {
    //       debugInfo.sessionStorageKeys = Object.keys(window.sessionStorage);
    //     }
    //     if (hasWindow && window.localStorage) {
    //       debugInfo.localStorageKeys = Object.keys(window.localStorage);
    //     }

    //     // Single structured log for easier inspection
    //     console.log('OIDC state debug info:', debugInfo);
    //   } catch (e) {
    //     console.warn('Failed to log OIDC state debug info:', e);
    //   }
    // }

    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center',
        fontFamily: 'Arial, sans-serif',
        maxWidth: '600px',
        margin: '50px auto'
      }}>
        <h2 style={{ color: '#d32f2f', marginBottom: '1rem' }}>Authentication Error</h2>
        <p style={{ marginBottom: '1rem' }}>
          {auth.error.message || 'Unable to authenticate. Please check your configuration.'}
        </p>
        <p style={{ fontSize: '0.9rem', color: '#666' }}>
          If you're using placeholder credentials, set <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>REACT_APP_DEV_MODE=true</code> in your .env file to enable development mode.
        </p>
      </div>
    );
  }

  return (
    <Routes>
      {/* Landing / Public Routes */}
      <Route path="/home" element={<LandingPage />} />

      {/* Callback Route */}
      <Route path="/callback" element={<CallbackPage />} /> 
      <Route path="/AccessWCAGCostEstimator" element={<EstimatorRedirect />} />

      {/* Protected App Routes */}
      <Route
        path="/app"
        element={
          auth.isAuthenticated ? (
            <MainApp
              isLoggingOut={isLoggingOut}
              setIsLoggingOut={setIsLoggingOut}
            />
          ) : (
            <Navigate to="/home" replace />
          )
        }
      />

      {/* Fallback: redirect unknown paths to /home */}
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

// Dev mode routes - allow access to all pages without authentication
function DevAppRoutes() {
  // Import MainApp for dev mode access
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  return (
    <Routes>
      <Route path="/home" element={<LandingPage />} />
      <Route path="/callback" element={<CallbackPage />} />
      <Route path="/AccessWCAGCostEstimator" element={<EstimatorRedirect />} />
      {/* Allow access to /app in dev mode without authentication */}
      <Route
        path="/app"
        element={
          <MainApp
            isLoggingOut={isLoggingOut}
            setIsLoggingOut={setIsLoggingOut}
          />
        }
      />
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

function App() {
  // In dev mode with placeholders, use a minimal AuthProvider config that won't crash
  // but will allow the UI to render
  if (isDevMode && isPlaceholderConfig) {
    // Create a minimal auth config that won't try to connect
    const devAuthConfig = {
      authority: `https://cognito-idp.${process.env.REACT_APP_AWS_REGION || 'us-east-1'}.amazonaws.com/dummy`,
      client_id: 'dev-mode',
      redirect_uri: `${baseUrl}/callback`,
      post_logout_redirect_uri: `${baseUrl}/home`,
      response_type: 'code',
      scope: 'email openid phone profile',
      // Disable automatic signin to prevent errors
      automaticSilentRenew: false,
      loadUserInfo: false,
    };
    
    return (
      <AuthProvider {...devAuthConfig}>
        <ThemeProvider theme={theme}>
          {isMaintenanceMode ? <MaintenancePage /> : <DevAppRoutes />}
        </ThemeProvider>
      </AuthProvider>
    );
  }

  // If config is invalid, show error without AuthProvider
  if (!isConfigValid || !cognitoAuthConfig) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ 
          padding: '2rem', 
          textAlign: 'center',
          fontFamily: 'Arial, sans-serif',
          maxWidth: '600px',
          margin: '50px auto'
        }}>
          <h2 style={{ color: '#d32f2f', marginBottom: '1rem' }}>Configuration Error</h2>
          <p style={{ marginBottom: '1rem' }}>
            Missing required environment variables for authentication.
          </p>
          <p style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#666' }}>
            Please create a <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>.env</code> file 
            in the <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>pdf_ui/</code> directory 
            with the required AWS Cognito configuration values.
          </p>
          <p style={{ fontSize: '0.85rem', color: '#666' }}>
            See <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>config.env.example</code> 
            in the parent directory for reference.
          </p>
          <p style={{ marginTop: '1.5rem', fontSize: '0.85rem', color: '#999' }}>
            Required variables: REACT_APP_AUTHORITY, REACT_APP_USER_POOL_CLIENT_ID, REACT_APP_HOSTED_UI_URL
          </p>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <AuthProvider {...cognitoAuthConfig}>
      <ThemeProvider theme={theme}>
          {/* <AppRoutes /> */}
          {isMaintenanceMode ? <MaintenancePage /> : <AppRoutes />}
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
